import { datadogRum } from "@datadog/browser-rum";
import TagManager from "@sooro-io/react-gtm-module";

// constants
import { DD_RUM_AXEPTIO_KEY } from "@/constants/datadog.constants.ts";
import { type UserEntityPreferredLanguage } from "./api/__generated__/orval/auth";
import { JWT_EXPIRED_ERROR_MESSAGE } from "./constants/jwt.constants";
import { getAxeptioVendorCookie } from "./helpers/getAxpetioVendorCookie";

const { isVendorActive: isDatadogActive } =
  getAxeptioVendorCookie(DD_RUM_AXEPTIO_KEY);

type VendorPermissions = {
  isDatadogActive: boolean;
};

const currentVendorPermissions: VendorPermissions = {
  isDatadogActive,
};

export const setAxeptioSettings = (lang?: UserEntityPreferredLanguage) => {
  const axeptioClientId = import.meta.env.VITE_AXEPTIO_CLIENT_ID;

  if (!axeptioClientId || !window.axeptioSDK) {
    return;
  }

  const userLang = lang || navigator.language.slice(0, 2);
  const axceptiocookieVersion: Record<string, string> = {
    en: "_en-EU",
    fr: "",
    it: "_it",
    de: "_de",
    es: "_es",
    gr: "_gr",
    pl: "_pl",
    hr: "_hr",
    sl: "_sl",
  };

  const cookiesVersion = `marketplace${axceptiocookieVersion[userLang] || ""}`;

  // Wrap in try/catch to handle race condition where SDK exists but client isn't ready
  try {
    window.axeptioSDK.setCookiesVersion(cookiesVersion);
  } catch {
    // SDK not fully initialized yet, retry after a short delay
    setTimeout(() => {
      try {
        window.axeptioSDK?.setCookiesVersion(cookiesVersion);
      } catch {
        console.warn("Axeptio: Failed to set cookies version after retry");
      }
    }, 1000);
  }
};

const initializeOrUpdateDatadog = (): void => {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DD_APP_ID,
    clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN,
    site: "datadoghq.eu",
    service: "kazaar-app",
    version: import.meta.env.VITE_APP_VERSION,
    env: import.meta.env.VITE_DD_ENV,
    allowedTracingUrls: [/^https:\/\/[^/]+\.kazaar\.com/],
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: "mask-user-input",
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    beforeSend: (event) => {
      // Filter out JWT expired errors (handled by refresh token mechanism)
      if (event.type === "error") {
        const errorMessage = event.error?.message || "";
        if (errorMessage.includes(JWT_EXPIRED_ERROR_MESSAGE)) {
          return false;
        }
      }
      return true;
    },
  });

  datadogRum.startSessionReplayRecording();
};

if (
  currentVendorPermissions.isDatadogActive &&
  import.meta.env.VITE_DD_ENV?.toLowerCase() !== "local"
) {
  initializeOrUpdateDatadog();
}

// TODO : Check with ops if we can pass it to env
if (import.meta.env.PROD && import.meta.env.VITE_GTM_ID) {
  TagManager.initialize({
    gtmId: import.meta.env.VITE_GTM_ID,
  });
}

// init DataLayer for Google Tag manager
if (!window.dataLayer) {
  window.dataLayer = [];
}

if (import.meta.env.MODE !== "development") {
  const setupAxeptioListeners = (isRetry = false) => {
    try {
      if (window.axeptioSDK) {
        window.axeptioSDK.on("cookies:complete", (choices) => {
          try {
            currentVendorPermissions.isDatadogActive = Boolean(
              choices[DD_RUM_AXEPTIO_KEY]
            );

            if (
              currentVendorPermissions.isDatadogActive &&
              import.meta.env.VITE_DD_ENV?.toLowerCase() !== "local"
            ) {
              initializeOrUpdateDatadog();
            }
          } catch (error) {
            console.warn("Axeptio: Failed to process consent choices", error);
          }
        });
        return;
      }
      if (!isRetry) {
        // Retry once after 2 seconds if SDK is not ready
        setTimeout(() => {
          setupAxeptioListeners(true);
        }, 2000);
        return;
      } else {
        console.error("Axeptio SDK failed to load after retry");
      }
    } catch (error) {
      if (!isRetry) {
        setTimeout(() => {
          setupAxeptioListeners(true);
        }, 2000);
        return;
      }
      console.error(
        "Axeptio: Failed to setup event listener after retry",
        error
      );
    }
  };

  setupAxeptioListeners();
}
